import React, { useState, useEffect, useMemo } from 'react';
import { Box, Button, Flex, Heading, Text } from '@chakra-ui/react';
import { ProjectCard } from './ProjectCard';
import {
  deriveAtRiskTier,
  deriveDaysFromEnd,
  isStalled,
  stalledCalendarDays,
} from './utils';
import { ManagerHomeProject, ManagerHomeSection, SECTION_LABELS } from './types';

interface SectionProps {
  section: ManagerHomeSection;
  projects: ManagerHomeProject[];
  onRowClick: (project: ManagerHomeProject) => void;
  onReassign: (project: ManagerHomeProject) => void;
  onArchive: (project: ManagerHomeProject) => void;
  onMarkComplete: (project: ManagerHomeProject) => void;
  pageSize?: number;
}

export const Section: React.FC<SectionProps> = ({
  section,
  projects,
  onRowClick,
  onReassign,
  onArchive,
  onMarkComplete,
  pageSize = 5,
}) => {
  const [expanded, setExpanded] = useState(false);

  const projectsKey = useMemo(() => projects.map((p) => p.id).join(','), [projects]);
  useEffect(() => {
    setExpanded(false);
  }, [projectsKey]);

  const visible: ManagerHomeProject[] = expanded ? projects : projects.slice(0, pageSize);

  return (
    <Box data-testid={`manager-home-section-${section}`} mb="2rem">
      <Flex alignItems="baseline" gap="25px" mb="0.75rem">
        <Heading as="h2" size="md">
          {SECTION_LABELS[section]}
        </Heading>
        <Text fontSize="sm" color="gray.500">
          {projects.length} {projects.length === 1 ? 'Project' : 'Projects'}
        </Text>
      </Flex>

      {projects.length === 0 ? (
        <Box
          data-testid="manager-home-section-empty"
          p="1.5rem"
          textAlign="center"
          color="gray.500"
          border="1px dashed"
          borderColor="gray.300"
          borderRadius="4px"
        >
          <Text fontSize="sm">No projects in this section right now.</Text>
        </Box>
      ) : (
        <>
          <Flex direction="column" gap="12px">
            {visible.map((p: ManagerHomeProject, idx: number) => (
              <ProjectCard
                key={p.id}
                rank={idx + 1}
                projectName={p.name}
                groupLabel={p.groupLabel}
                purpose={p.purpose ?? ''}
                endDate={p.endDate}
                atRiskTier={deriveAtRiskTier(p.endDate)}
                isStalled={isStalled(p.lastActivityAt ?? null)}
                stalledDays={stalledCalendarDays(p.lastActivityAt ?? null)}
                section={section}
                totalCount={p.totalCount}
                completeCount={p.completeCount}
                approvedCount={p.approvedCount}
                daysFromEnd={deriveDaysFromEnd(p.endDate)}
                isMultiGrader={p.isMultiGrader ?? null}
                codeTypes={p.codeTypes ?? []}
                rubricName={p.rubricName ?? null}
                graders={p.graders ?? []}
                completed={p.completed ?? false}
                onClick={() => onRowClick(p)}
                onReassign={() => onReassign(p)}
                onArchive={() => onArchive(p)}
                onMarkComplete={() => onMarkComplete(p)}
              />
            ))}
          </Flex>
          {projects.length > pageSize && (
            <Box py="0.5rem" textAlign="center">
              <Button
                variant="link"
                size="sm"
                onClick={() => setExpanded((prev) => !prev)}
              >
                {expanded
                  ? 'Show less'
                  : `Show more (${projects.length - pageSize} more)`}
              </Button>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

Section.displayName = 'Section';
export default Section;
